import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Euro, Car } from "lucide-react";

const VEHICLE_LABELS = {
  standard: { label: "Standard", desc: "Sedán ejecutivo (hasta 3 pax)" },
  minivans: { label: "Mini Van", desc: "Furgoneta 7 plazas" },
  minibus: { label: "Mini Bus", desc: "Autobús pequeño 14 plazas" },
};

const DEFAULTS = {
  standard: { base: 35, per_km: 1.2 },
  minivans: { base: 50, per_km: 1.4 },
  minibus: { base: 140, per_km: 2.8 },
};

export default function PricingPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pricing");
      const v = {};
      Object.entries(data.vehicles || {}).forEach(([k, row]) => {
        v[k] = { base: String(row.base), per_km: String(row.per_km) };
      });
      setValues(v);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, field, val) =>
    setValues((v) => ({ ...v, [k]: { ...v[k], [field]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { values: {} };
      Object.entries(values).forEach(([k, row]) => {
        payload.values[k] = {
          base: parseFloat(row.base),
          per_km: parseFloat(row.per_km),
        };
      });
      const { data } = await api.patch("/pricing", payload);
      const v = {};
      Object.entries(data.vehicles || {}).forEach(([k, row]) => {
        v[k] = { base: String(row.base), per_km: String(row.per_km) };
      });
      setValues(v);
      toast.success("Precios actualizados");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    const v = {};
    Object.entries(DEFAULTS).forEach(([k, row]) => {
      v[k] = { base: String(row.base), per_km: String(row.per_km) };
    });
    setValues(v);
    toast.info("Precios cargados a los valores por defecto — pulsa Guardar para aplicarlos");
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-zinc-500" data-testid="pricing-loading">
        <Loader2 className="animate-spin" size={16} /> Cargando precios…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">
          Centro de Control · Administración
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          Precios globales
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Estos son los precios que se aplican en la reserva pública, la calculadora del staff y
          la estimación en el chat de SofIA. Regla vigente: <b>≤ 30&nbsp;km</b> se cobra sólo la
          <b> tarifa base</b>; <b>&gt; 30&nbsp;km</b> se cobra <b>km × €/km</b> (sin base).
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.keys(VEHICLE_LABELS).map((k) => (
          <div
            key={k}
            className="border-2 border-black bg-white p-5"
            data-testid={`pricing-card-${k}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 bg-black text-white flex items-center justify-center">
                <Car size={16} />
              </div>
              <div className="font-display text-lg font-bold">{VEHICLE_LABELS[k].label}</div>
            </div>
            <div className="text-xs text-zinc-500 mb-4">{VEHICLE_LABELS[k].desc}</div>

            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest flex items-center gap-1">
                  <Euro size={11} /> Tarifa base (≤ 30 km)
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={values[k]?.base ?? ""}
                    onChange={(e) => set(k, "base", e.target.value)}
                    className="h-11 rounded-none border-black pr-8"
                    data-testid={`pricing-${k}-base`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">
                    €
                  </span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest flex items-center gap-1">
                  <Euro size={11} /> Precio por km (&gt; 30 km)
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={values[k]?.per_km ?? ""}
                    onChange={(e) => set(k, "per_km", e.target.value)}
                    className="h-11 rounded-none border-black pr-12"
                    data-testid={`pricing-${k}-per-km`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">
                    €/km
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={resetDefaults}
          className="h-12 rounded-none border-2 border-black text-xs uppercase tracking-widest"
          data-testid="pricing-reset-btn"
        >
          <RotateCcw size={14} className="mr-2" /> Restaurar por defecto
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={saving}
          className="h-12 rounded-none btn-primary px-8 text-xs uppercase tracking-widest"
          data-testid="pricing-save-btn"
        >
          {saving ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <>
              <Save size={14} className="mr-2" /> Guardar cambios
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
